import { init, replayIntegration } from '@sentry/browser';

// Sentry configuration for production environment
export const initSentry = () => {
  const DSN =
    process.env.REACT_APP_SENTRY_DSN ||
    'https://e0e474a0fab14f919147bc25cd32429a@o38629.ingest.us.sentry.io/1341698';
  const ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'production';
  const RELEASE = process.env.REACT_APP_VERSION || 'unknown';
  const DIST = process.env.REACT_APP_BUILD || undefined;

  if (!DSN) {
    // eslint-disable-next-line no-console
    console.warn('Sentry DSN not set. Sentry will not be initialized.');
    return;
  }
  if (!ENV) {
    // eslint-disable-next-line no-console
    console.warn('Sentry ENV not set. Sentry will not be initialized.');
    return;
  }

  // Solo habilitar en producción o staging
  if (!['production', 'staging'].includes(ENV)) {
    // eslint-disable-next-line no-console
    console.log(`Sentry disabled for env: ${ENV}`);
    return;
  }

  init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    dist: DIST,
    integrations: [
      replayIntegration({
        networkDetailAllowUrls: [window.location.origin],
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions
    replaysSessionSampleRate: 0.05, // 5% of sessions
    replaysOnErrorSampleRate: 0.2, // 20% of error sessions

    // Advanced filtering to reduce noise
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Skip errors from browser extensions
      if (isExtensionError(event)) {
        return null;
      }

      // Skip network errors we don't care about
      if (error?.isAxiosError && shouldIgnoreNetworkError(error)) {
        return null;
      }

      // Skip known noisy errors
      if (isNoiseError(error, event)) {
        return null;
      }

      // Skip Sentry own errors (rate limit, auth, etc)
      const sentryUrl = event.request?.url || error?.config?.url || '';
      if (sentryUrl.includes('sentry.io')) {
        return null;
      }
      // Skip Sentry HTTP errors
      const sentryStatus = error?.response?.status;
      if (
        sentryUrl.includes('sentry.io') &&
        [429, 403, 401, 400, 404].includes(sentryStatus)
      ) {
        return null;
      }
      if (sentryUrl.includes('sentry.io') && sentryStatus >= 500) {
        return null;
      }

      // Add additional context for debugging
      if (error?.isAxiosError) {
        event.tags = {
          ...event.tags,
          errorType: 'network',
          httpStatus: error.response?.status,
          httpMethod: error.config?.method?.toUpperCase(),
        };

        event.extra = {
          ...event.extra,
          url: error.config?.url,
          requestData: error.config?.data,
        };
      }

      return event;
    },

    // Ignore specific error patterns
    ignoreErrors: [
      // Browser extension errors
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',

      // Common browser/network errors
      'Network Error',
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'Script error',
      'Non-Error promise rejection captured',
      'ResizeObserver loop limit exceeded',
      'ChunkLoadError',
      'Loading chunk',
      'Loading CSS chunk',

      // Common React errors that aren't actionable
      'Warning: ',
      'Minified React error',
    ],

    // Set default tags
    initialScope: {
      tags: {
        component: 'b4b-react',
        version: RELEASE,
      },
    },
  });
  // eslint-disable-next-line no-console
  console.log(`Sentry initialized for ${ENV}`);
};

// Helper function to detect browser extension errors
const isExtensionError = event => event.exception?.values?.[0]?.stacktrace?.frames?.some(
  frame =>
    frame.filename?.includes('extension://') ||
    frame.filename?.includes('moz-extension://') ||
    frame.filename?.includes('safari-extension://') ||
    frame.filename?.includes('chrome-extension://')
);

// Helper function to determine if we should ignore network errors
const shouldIgnoreNetworkError = error => {
  const status = error.response?.status;
  const url = error.config?.url || '';
  const method = error.config?.method || '';

  // Always ignore 404s
  if (status === 404) {
    return true;
  }

  // Ignore expected auth errors
  if (
    [400, 401, 403].includes(status) &&
    (url.includes('/auth/') ||
      url.includes('/login/') ||
      url.includes('/oauth/') ||
      url.includes('/signup/'))
  ) {
    return true;
  }

  // Ignore timeouts on known slow endpoints
  if (
    error.code === 'ECONNABORTED' &&
    (url.includes('/upload/') || url.includes('/export/') || url.includes('/download/'))
  ) {
    return true;
  }

  // Ignore client errors (4xx) or any OPTIONS request
  if ((status >= 400 && status < 500) || method === 'OPTIONS') {
    return true;
  }

  return false;
};

// Helper function to detect noisy errors
const isNoiseError = (error, event) => {
  const errorMessage = error?.message || event.message || '';

  const noisePatterns = [
    /network error/i,
    /timeout/i,
    /aborted/i,
    /cancelled/i,
    /script error/i,
    /non-error promise rejection/i,
    /resizeobserver loop limit exceeded/i,
    /chunkloaderror/i,
    /loading chunk \d+ failed/i,
    /loading css chunk \d+ failed/i,
  ];

  return noisePatterns.some(pattern => pattern.test(errorMessage));
};

export default initSentry;
